import React from 'react';
import Box from '@elementor/ui/Box';
import Typography from '@elementor/ui/Typography';
import Link from '@elementor/ui/Link';
import { PlayerPlayIcon } from '@elementor/icons';
import { pushDataLayerEvent } from '../helpers';

/**
 * Renders the agent's `videos` array as a list of links to the
 * corresponding Elementor video pages.
 *
 * Shape of `videos`: `[{ title, link }]`, max `RAG_VIDEOS_TOP_K`
 * items (default 5), ordered closest-match-first. May be empty.
 *
 * `title` is the canonical video title extracted from the JSONL `title`
 * field on the agent side (see `_VIDEO_TITLE_RE` in runtime.py). A
 * defensive fallback to "Watch video" handles the rare case where the
 * agent emits an empty title (corpus ingestion bug).
 *
 * No thumbnail today — Elementor video pages don't expose a stable
 * thumbnail URL we can consume from the client without scraping. The
 * Watch link routes to the canonical /videos/<slug>/ page where the
 * real player and metadata live.
 */
function RelatedMedia({ videos }) {
  if (!videos || videos.length === 0) {
    return null;
  }

  const heading = videos.length === 1 ? 'Related video' : 'Related videos';

  return (
    <Box className="ft-related-videos" sx={{ mt: 2, mb: 2 }}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 1, fontWeight: 'medium' }}
      >
        {heading}
      </Typography>
      {videos.map((video, index) => (
        <Box
          key={video.link || index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 0.5,
          }}
        >
          <PlayerPlayIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          <Link
            href={video.link}
            target="_blank"
            rel="noopener noreferrer"
            color="primary"
            underline="hover"
            variant="body2"
            onClick={() => {
              const videoTitle = video.title || 'Watch video';
              pushDataLayerEvent({
                section: 'in page',
                element_name: 'sends related video',
                outcome: 'proceeding to a related video',
                additional_description: videoTitle,
                english_text: videoTitle,
                type: 'navigation',
                link_text: videoTitle,
                link_url: video.link || '',
              });
            }}
          >
            {video.title || 'Watch video'}
          </Link>
        </Box>
      ))}
    </Box>
  );
}

export default RelatedMedia;
